<template>
  <Select
    :pt="{ label: { class: '!text-sm' } }"
    v-model="selectedValue"
    :options="items"
    :option-label="optionLabel"
    :option-value="optionValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :show-clear="showClear"
    :loading="loading && !items.length"
    :filter="filter"
    :filter-placeholder="filterPlaceholder ?? languageStore.t('general.search')"
    :filter-fields="filterFields"
    :empty-message="loading ? loadingMessage : emptyMessage"
    :empty-filter-message="loading ? loadingMessage : emptyMessage"
    :virtual-scroller-options="{
      lazy: true,
      onLazyLoad: onLazyLoad,
      items: items,
      loading: loading,
      itemSize: itemSize,
      showLoader: true,
      appendOnly: true,
      step: pageSize,
    }"
    auto-filter-focus
    @filter="onFilter"
    @before-show="onBeforeShow"
    @hide="onHide"
    @change="onChange"
  >
    <template #value="slotProps">
      <span v-if="displayLabel">{{ displayLabel }}</span>
      <span v-else>{{ slotProps.placeholder }}</span>
    </template>
  </Select>
</template>

<script lang="ts" setup>
import { useLanguageStore } from '@/stores/LanguageStore'
import debounce from 'lodash/debounce'
import { Select, VirtualScrollerLazyEvent } from 'primevue'
import { computed, onBeforeUnmount, ref, watch } from 'vue'

export interface LazySelectPage<T> {
  listOfItems: T[]
  page: number
  numberOfPages: number
  numberOfItems: number
}

export interface LazySelectFetchParams {
  page: number
  pageSize: number
  search: string
  signal: AbortSignal
}

const props = withDefaults(
  defineProps<{
    modelValue?: string | number | null
    fetcher: (params: LazySelectFetchParams) => Promise<LazySelectPage<any>>
    resolveSelected?: (value: string | number) => Promise<any | null>
    optionLabel: string | ((item: any) => string)
    optionValue?: string
    filterFields?: string[]
    placeholder?: string
    filterPlaceholder?: string
    emptyMessage?: string
    loadingMessage?: string
    pageSize?: number
    itemSize?: number
    disabled?: boolean
    showClear?: boolean
    filter?: boolean
  }>(),
  {
    pageSize: 20,
    itemSize: 40,
    filter: true,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | null): void
  (e: 'change'): void
  (e: 'error', error: unknown): void
}>()

const languageStore = useLanguageStore()

const items = ref<any[]>([])
const loading = ref(false)
const search = ref('')
const page = ref(1)
const numberOfPages = ref(0)
const selectedOption = ref<any>(null)

let abortController: AbortController | null = null

const selectedValue = computed({
  get: () => props.modelValue ?? null,
  set: (value) => emit('update:modelValue', value),
})

const resolveValue = (item: any) => (props.optionValue ? item?.[props.optionValue] : item)

const resolveLabel = (item: any) =>
  typeof props.optionLabel === 'function' ? props.optionLabel(item) : item?.[props.optionLabel]

const displayLabel = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined) return ''

  const option =
    items.value.find((item) => resolveValue(item) === props.modelValue) ?? selectedOption.value

  if (!option || resolveValue(option) !== props.modelValue) return ''

  return resolveLabel(option)
})

async function load(targetPage: number, append: boolean) {
  if (abortController) abortController.abort()

  abortController = new AbortController()
  const signal = abortController.signal

  loading.value = true

  try {
    const result = await props.fetcher({
      page: targetPage,
      pageSize: props.pageSize,
      search: search.value,
      signal,
    })

    if (signal.aborted) return

    page.value = result?.page ?? targetPage
    numberOfPages.value = result?.numberOfPages ?? 0

    const loaded = result?.listOfItems ?? []
    items.value = append ? [...items.value, ...loaded] : loaded
  } catch (error) {
    if (signal.aborted) return

    if (!append) items.value = []
    emit('error', error)
  } finally {
    if (!signal.aborted) loading.value = false
  }
}

const onFilter = debounce((event: { value: string }) => {
  search.value = event.value ?? ''
  load(1, false)
}, 400)

function onLazyLoad(event: VirtualScrollerLazyEvent) {
  if (loading.value) return
  if (page.value >= numberOfPages.value) return
  if (event.last < items.value.length) return

  load(page.value + 1, true)
}

function onBeforeShow() {
  if (!items.value.length) load(1, false)
}

function onChange(event: { value: any }) {
  const option = items.value.find((item) => resolveValue(item) === event.value)
  if (option) selectedOption.value = option
  emit('change')
}

function onHide() {
  onFilter.cancel()
  search.value = ''
  items.value = []
  page.value = 1
  numberOfPages.value = 0
}

async function resolveSelectedOption() {
  const value = props.modelValue

  if (value === null || value === undefined) {
    selectedOption.value = null
    return
  }

  const fromItems = items.value.find((item) => resolveValue(item) === value)
  if (fromItems) {
    selectedOption.value = fromItems
    return
  }

  if (selectedOption.value && resolveValue(selectedOption.value) === value) return
  if (!props.resolveSelected) return

  try {
    const resolved = await props.resolveSelected(value)
    if (resolved && props.modelValue === value) selectedOption.value = resolved
  } catch (error) {
    emit('error', error)
  }
}

watch([() => props.modelValue, items], resolveSelectedOption, { immediate: true })

onBeforeUnmount(() => {
  onFilter.cancel()
  if (abortController) abortController.abort()
})

defineExpose({
  refresh: () => load(1, false),
})
</script>
